// web/src/features/team/player/RosterPlayerModalContent.tsx
/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import { useFormikContext } from 'formik'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { type RosterPlayerFormValues } from '@sportninja/common/features/team/player'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import useWindowSize from 'src/hooks/useWindowSize'
import { useSidebarState } from 'src/hooks/useSidebarState'
import { ScrollSpyLayout } from 'src/components/ScrollSpyLayout'
import { FullScreenModalHeader } from 'src/components/FullScreenModal'
import ConfirmDiscardChangesModal from 'src/features/profile/player/setupModal/components/ConfirmDiscardChangesModal'
import { useSectionEdit } from 'src/features/profile/player/setupModal/context/SectionEditContext'
import { useGuardedClose } from 'src/features/profile/player/setupModal/hooks/useGuardedClose'
import { useModalBreadcrumbs } from 'src/features/profile/player/setupModal/hooks/useModalBreadcrumbs'
import type { ModalTrailItem } from 'src/features/profile/player/setupModal/types/modalTrail'
import HeaderCancelIcon from 'src/features/profile/player/setupModal/components/HeaderCancelIcon'
import HeaderSaveIcon from 'src/features/profile/player/setupModal/components/HeaderSaveIcon'
import PlayerFormSkeleton from 'src/features/profile/player/setupModal/components/PlayerFormSkeleton'
import PersonalSection from 'src/features/profile/player/setupModal/components/sections/PersonalSection'
import AddressSection from 'src/features/profile/player/setupModal/components/sections/AddressSection'
import EmergencyContactSection from 'src/features/profile/player/setupModal/components/sections/EmergencyContactSection'
import SocialProfilesSection from 'src/features/profile/player/setupModal/components/sections/SocialProfilesSection'
import MembershipSection from 'src/features/profile/player/setupModal/components/sections/MembershipSection'
import ProfileExtraSection from 'src/features/profile/player/setupModal/components/sections/ProfileExtraSection'
import SalesforceIdChip from './components/SalesforceIdChip'
import RosterInfoSection from './components/sections/RosterInfoSection'
import FamilyMembersSection from 'src/features/profile/family/components/FamilyMembersSection'
import { getRosterSetupItems } from './utils/getRosterSetupItems'
import { fullScreenOverlayBounds } from 'src/components/FullScreenModal/utils/fullScreenOverlayBounds'
import {
  FULL_SCREEN_PAGE_GUTTER,
  FULL_SCREEN_PAGE_MAX_WIDTH,
} from 'src/components/FullScreenModal/constants'
import { useRosterPlayerContext } from './RosterPlayerContext'

type Props = {
  isVisible: boolean
  transitionDuration: number
  /** SN-8449 — the overlay this editor is opened from, if any (see RosterPlayerModal). */
  hostZIndex?: number
  /** The entry point's breadcrumb trail ("Team › Roster"). */
  trail?: ModalTrailItem[]
}

const RosterPlayerModalContent: React.FC<Props> = ({
  isVisible,
  transitionDuration,
  hostZIndex,
  trail,
}) => {
  const { width } = useWindowSize()
  const isMobile = width <= 768
  const [sidebarCollapsed] = useSidebarState()
  const sidebarWidth = isMobile ? 0 : sidebarCollapsed ? 64 : 284
  const bounds = fullScreenOverlayBounds({
    hostZIndex,
    isMobile,
    sidebarWidth,
  })

  const {
    isLoading,
    playerName,
    isSeeding,
    onCancel,
    sportId,
    hasAdminPermission,
    requireEmail,
    isEdit,
    isAddExisting,
    emailLocked,
    namesLocked,
    salesforceIdLocked,
    showFamilySection,
    teamId,
    rosterId,
    familyPlayer,
  } = useRosterPlayerContext()
  const { submitForm, dirty } = useFormikContext<RosterPlayerFormValues>()
  // Edit mode only (null when adding): which section is open for editing.
  const sectionEdit = useSectionEdit()

  // Unsaved-changes guard: warn before discarding edits on Back/Cancel/Close
  // or a breadcrumb; close straight away when the form is untouched. In edit
  // mode only an open section can hold edits.
  const hasUnsavedEdits = sectionEdit
    ? Boolean(sectionEdit.editingSection) && dirty
    : dirty
  const { requestClose, isConfirmOpen, confirmDiscard, cancelDiscard } =
    useGuardedClose({ hasUnsavedEdits, onClose: onCancel })
  const breadcrumbs = useModalBreadcrumbs(trail, requestClose)

  const handleSave = async () => {
    await submitForm()
  }

  return (
    <div
      css={css`
        ${overlayStyle}
        z-index: ${bounds.zIndex};
        top: ${bounds.top}px;
        left: ${bounds.left}px;
        opacity: ${isVisible ? 1 : 0};
        transform: translateY(${isVisible ? '0' : '20px'});
        transition: opacity ${transitionDuration}ms ease-out,
          transform ${transitionDuration}ms ease-out, left 0.2s ease-in-out;
      `}
    >
      <div css={contentWrapperStyle}>
        {isEdit ? (
          // Edit is Player Details: caption over the player's name, and Close —
          // each section saves on its own.
          <FullScreenModalHeader
            breadcrumbs={breadcrumbs.items}
            onHome={breadcrumbs.onHome}
            caption={playerName ? capitalizeWords('Player Details') : undefined}
            title={playerName || capitalizeWords('Player Details')}
            actions={
              <HeaderActionButton
                text={capitalizeWords('Close')}
                onClick={() => requestClose()}
                variant='secondary'
                textWeight='medium'
                disabled={isLoading}
                icon={<HeaderCancelIcon />}
              />
            }
          />
        ) : (
          <FullScreenModalHeader
            breadcrumbs={breadcrumbs.items}
            onHome={breadcrumbs.onHome}
            onBack={() => requestClose()}
            title={capitalizeWords('Add Player')}
            actions={
              <>
                <HeaderActionButton
                  text='Cancel'
                  onClick={() => requestClose()}
                  variant='secondary'
                  textWeight='medium'
                  disabled={isLoading}
                  icon={<HeaderCancelIcon />}
                />
                <HeaderActionButton
                  text='Save'
                  onClick={handleSave}
                  variant='primary'
                  textWeight='bold'
                  disabled={isLoading}
                  isLoading={isLoading}
                  icon={<HeaderSaveIcon />}
                />
              </>
            }
          />
        )}

        {/* A section save keeps the edit form on screen; adding shows the
            skeleton through the save, as before. */}
        {isSeeding || (isLoading && !isEdit) ? (
          <PlayerFormSkeleton stacked={isMobile} />
        ) : (
          <ScrollSpyLayout
            items={getRosterSetupItems({
              isAddExisting,
              showFamilySection,
              isEdit,
            })}
            stacked={isMobile}
            gap={24}
          >
            <ScrollSpyLayout.Sidebar
              width={256}
              ariaLabel='Player form categories'
            />
            <ScrollSpyLayout.Content
              ariaLabel='Roster player sections'
              paddingRight={16}
            >
              <PersonalSection
                requiredNameFields
                requireEmail={requireEmail}
                readOnlyExisting={isAddExisting}
                // Lock email + name only once the player has accepted their
                // invite (identity). Add-new and invited-but-unaccepted keep
                // them editable; add-existing is already read-only.
                lockEmail={emailLocked}
                lockNames={namesLocked}
                // Only while the email is actually editable — a locked or
                // read-only email isn't triggering an invite from this form.
                emailHelperText={
                  isAddExisting || emailLocked
                    ? undefined
                    : 'Adding an email address will send this player an invite to join the team.'
                }
                // Not an incidental hint: filling this field sends the player
                // an invite, so it reads in the SportNinja amber rather than
                // the muted default. PRIMARY_300 (not PRIMARY): the amber is
                // fixed on both tenants here, and Canlan's own blue PRIMARY is
                // unreadable on this dark surface (2.26:1).
                emailHelperTextColor={colors.PRIMARY_300}
                // Canlan-only, saved-value-only; renders null otherwise.
                extraIdChips={<SalesforceIdChip />}
              />
              <RosterInfoSection sportId={sportId} />
              {!isAddExisting ? (
                <>
                  <AddressSection />
                  <EmergencyContactSection />
                  {showFamilySection && familyPlayer ? (
                    <FamilyMembersSection
                      playerId={familyPlayer.id}
                      scope={{ kind: 'roster', teamId, rosterId }}
                      player={familyPlayer}
                    />
                  ) : null}
                  {/* Player Details' extra sections (edit and add-new). */}
                  <ProfileExtraSection
                    sectionKey='experience'
                    showReadOnlyFields={isEdit}
                  />
                  <ProfileExtraSection
                    sectionKey='preferences'
                    showReadOnlyFields={isEdit}
                  />
                  <ProfileExtraSection
                    sectionKey='additional'
                    showReadOnlyFields={isEdit}
                  />
                  {/* Registration Notes only: shown on edit, empty on add. */}
                  <ProfileExtraSection
                    sectionKey='notes'
                    showReadOnlyFields={isEdit}
                  />
                  <SocialProfilesSection />
                </>
              ) : null}
              {/* Roster-form-only: Membership also carries the identifiers, so
                  it renders in add-existing too (where the player's own
                  membership values stay read-only on the BE). */}
              <MembershipSection
                showIdentifiers
                showCustomerIdentifier={hasAdminPermission}
                salesforceIdLocked={salesforceIdLocked}
              />
            </ScrollSpyLayout.Content>
          </ScrollSpyLayout>
        )}
      </div>

      <ConfirmDiscardChangesModal
        open={isConfirmOpen}
        onCancel={cancelDiscard}
        onConfirm={confirmDiscard}
      />
    </div>
  )
}

export default RosterPlayerModalContent

// ---- styled (cloned from EditPlayerModalContent for pixel parity) ----
const overlayStyle = css`
  position: fixed;
  right: 0;
  bottom: 0;
  background-color: ${colors.SECONDARY_400};
  display: flex;
  flex-direction: column;
`

// Same wide column as the Edit Player and Player Details modals
// (FullScreenModal's page width).
const contentWrapperStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: ${FULL_SCREEN_PAGE_MAX_WIDTH}px;
  margin: 0 auto;
  padding: 24px ${FULL_SCREEN_PAGE_GUTTER}px 0px;

  @media (max-width: 768px) {
    max-width: none;
    padding: 0;
  }
`
